<template>
  <Transition name="spot-overlay">
    <div v-if="isOpen" class="SpotOverlay" @click.self="handleClose">
      <div ref="drawerRef" class="spot-card" :class="{ 'is-mobile': isMobile }">
        <div class="spot-scroll">
          <div class="spot-header">
            <div v-if="location && primaryBoardMarks" class="spot-boards-header-btn" @click="handleBoardsIcon">
              <div class="spot-board-squares" />
              <div v-if="primaryBoardMarks.eligible" class="spot-board-dot is-eligible" />
              <div v-else-if="primaryBoardMarks.waiting" class="spot-board-dot is-waiting" />
              <div v-if="primaryBoardMarks.planted" class="spot-board-dot is-planted" />
              <div v-if="primaryBoardMarks.played" class="spot-board-dot is-played" />
            </div>

            <LocationCircle
              v-if="circleLocation"
              :location="circleLocation"
              :selected="hasIrlActivity"
              :loading="isUploading"
              mode="none"
              class="spot-circle"
              @click="handleOpenGallery"
            />

            <div v-if="location" class="spot-meta">
              <div v-if="distanceFix" class="spot-distance">
                {{ getDistanceLabel(location.coordinates, distanceFix) }}
                <span v-if="awaitingLocation" class="spot-distance-refreshing">↻</span>
              </div>
            </div>

            <div v-if="location" class="spot-header-actions">
              <div
                v-if="authUser"
                class="spot-follow-header-btn"
                :class="{ following: location.favorite }"
                @click="handleToggleFollow"
              >
                <GamIcon :name="location.favorite ? GamIconName.HEART : GamIconName.HEART_EMPTY" is-current />
              </div>
              <GamMore v-bind="getMoreItems" />
            </div>
          </div>

          <template v-if="spotView === 'detail'">
            <div v-if="access.isRemote.value && location && !awaitingLocation" class="spot-remote-banner">
              <GamIcon :name="GamIconName.LOCATION_EMPTY" :size="GamIconSize.LARGE" is-current />
              <span>{{ localize('spot.remote.far_away') }}</span>
            </div>

            <template v-if="!access.isRemote.value">
              <div v-if="location" class="spot-actions">
                <div class="spot-action spot-action-play" @click="handlePlayChess">
                  <div class="spot-action-btn play-btn">
                    <GamIcon :name="GamIconName.PLAY_OUTLINE" :size="GamIconSize.X_LARGE" is-current />
                  </div>
                  <span class="spot-action-label">{{ localize('spot.actions.irl') }}</span>
                </div>

                <div class="spot-action spot-action-checkin" @click="handleCheckIn">
                  <div class="spot-checkin-btn" :class="{ 'is-on': location.isCheckedIn }">
                    <GamIcon :name="GamIconName.PIN" is-current />
                  </div>
                  <span class="spot-action-label" :class="{ 'is-on': location.isCheckedIn }">
                    {{ localize('spot.actions.checkin') }}
                  </span>
                </div>
              </div>

              <div v-if="location && activeCheckIns.length" class="spot-section spot-live-panel">
                <div class="spot-section-header">
                  <div class="spot-section-title">
                    <GamIcon :name="GamIconName.PIN" size="12px" is-current />
                    {{ localize('locations.detail.checkedIn') }}
                  </div>
                  <label v-if="location.isCheckedIn" class="spot-chess-set" @click.prevent="toggleChessSet">
                    <span class="spot-chess-check" :class="{ 'is-checked': hasChessSet }">
                      <GamIcon v-if="hasChessSet" :name="GamIconName.CHECK" :size="GamIconSize.SMALL" />
                    </span>
                    <GamIcon :name="GamIconName.CHESS_SET" :size="GamIconSize.LARGE" />
                    <span class="spot-chess-label">{{ localize('locations.detail.chessboard') }}</span>
                  </label>
                </div>
                <hr class="spot-section-line" />
                <CheckInsList
                  :active-check-ins="activeCheckIns"
                  :location="location"
                  :handshakes="location.handshakes"
                  :pending-handshakes="location.pendingHandshakes"
                  :user="authUser"
                  @update="locationStore.fetch(location!.id)"
                />
              </div>

              <LocationGambits v-if="location" :location="location" />

              <LocationChessFeatures v-if="location && location.chessFeatures?.length" :location="location" />

              <AboutLocation v-if="location" :detail="location" />

              <span v-if="completedBoards.length" class="spot-history-btn" @click="openHistory">
                {{ localize('spot.boards.history.label') }}
              </span>
            </template>
          </template>

          <template v-else-if="spotView === 'history'">
            <div class="spot-history-header">
              <div class="spot-history-back" @click="closeHistory">
                <GamIcon :name="GamIconName.ARROW_LEFT" size="14px" is-current />
              </div>
              <span class="spot-boards-title">{{ localize('spot.boards.history') }}</span>
            </div>
            <hr class="spot-section-line" />

            <div v-if="!completedBoards.length" class="spot-history-empty">
              {{ localize('spot.boards.history.empty') }}
            </div>

            <div v-else class="spot-history-list">
              <div
                v-for="board in completedBoards"
                :key="board.id"
                class="spot-history-item"
                @click="handleBoardClick(board.id)"
              >
                <div class="spot-history-result">{{ getResultLabel(board.result, board.fen) }}</div>
                <div class="spot-history-meta">
                  <span>{{ localizeWithValues('spot.boards.moves', [String(board.moveCount)]) }}</span>
                </div>
              </div>
            </div>
          </template>
        </div>

        <input
          ref="fileInputRef"
          type="file"
          accept=".jpg,.jpeg,.png"
          multiple
          style="display: none"
          @change="handleFilesSelected"
        />

        <GamExpandImage
          v-if="expandImageConfig"
          ref="imageExpandRef"
          v-bind="expandImageConfig"
          :can-add="!!authUser"
          :add-shortcut="!!authUser"
          :can-pin-cover="authStore.isAdmin()"
          :pinned-cover-hash="pinnedCoverHash"
          @close="expandImageConfig = null"
          @add="handleAddPhotoClick"
          @pin-cover="handlePinCover"
          @delete="handleDeletePhoto"
          can-report
          show-author
          show-likes
          is-gallery
        />

        <Transition name="proposal-drawer">
          <div v-if="isProposalOpen && proposalLocation" class="spot-proposal-drawer">
            <MapProposalCard
              :location="proposalLocation"
              :opening-hours="location?.openingHours"
              embedded
              @close="handleProposalClose"
              @created="handleProposalCreated"
            />
          </div>
        </Transition>

        <div class="spot-close" @click="handleClose">
          <GamIcon :name="GamIconName.X_CLOSE" :size="GamIconSize.X_LARGE" is-current />
        </div>
      </div>
    </div>
  </Transition>

  <ReportingDialog
    v-if="showReportDialog"
    :id="spotId!"
    :type="ReportType.LOCATION"
    @close="showReportDialog = false"
  />

  <GamDialog ref="spotQrDialog">
    <SpotQRCodeDialog v-if="location" :spot-name="location.name" :spot-id="location.id" />
  </GamDialog>
</template>

<script setup lang="ts">
import { ChessboardResult, type ChessboardDto } from '@/core/data/chessboard/chessboard.interface';
import type { BaseLocationDto, GeoCoords, ImageDto, MapLocationDto } from '@/core/data/location/location.interface';
import { MapLocationType } from '@/core/data/location/location.type';
import { BlobSource, ReportType } from '@/core/data/report/report.type';
import { gambits, localize, localizeWithValues } from '@/core/gambits';
import FormHelper from '@/core/helpers/form.helper';
import LocationHelper, { formatDistance, haversineDistance } from '@/core/helpers/location.helper';
import { isMobile } from '@/core/helpers/ui.helper';
import { useAdminStore } from '@/stores/admin.store';
import { useAuthStore } from '@/stores/auth.store';
import { useChessboardStore } from '@/stores/chessboard.store';
import { useGambitStore } from '@/stores/gambit.store';
import { useGeolocationStore } from '@/stores/geolocation.store';
import { useLocationStore } from '@/stores/location.store';
import { useMapStore } from '@/stores/map.store';
import { useNavigationStore } from '@/stores/navigation.store';
import { useSparkStore } from '@/stores/spark.store';
import ReportingDialog from '@/views/components/gam-dialog/dialog-contents/ReportingDialog.vue';
import SpotQRCodeDialog from '@/views/components/gam-dialog/dialog-contents/SpotQRCodeDialog.vue';
import GamDialog from '@/views/components/gam-dialog/GamDialog.vue';
import LocationCircle from '@/views/components/gam-location/LocationCircle.vue';
import GamMore from '@/views/components/gam-nav/gam-more/GamMore.vue';
import GamExpandImage from '@/views/components/GamExpandImage.vue';
import GamIcon from '@/views/components/GamIcon.vue';
import { GamButtonSize, GamButtonVariant } from '@/views/composables/constants/components/gamButton.constants';
import { GamIconName, GamIconSize } from '@/views/composables/constants/components/gamIcon.constants';
import { GamMorePosition } from '@/views/composables/constants/components/gamMore.constants';
import { useAccessLevel } from '@/views/composables/hooks/useAccessLevel';
import { useDrawerGesture } from '@/views/composables/hooks/useDrawerGesture';
import { rememberLocationFunction, requestLocationFor } from '@/views/composables/hooks/useLocationAsk';
import { notifyLocationDenied, notifyLocationUnavailable } from '@/views/composables/hooks/useLocationHelp';
import type { GamExpandImageType } from '@/views/composables/models/components/GamImage';
import type { GamMoreType } from '@/views/composables/models/components/GamMore';
import { findActiveCheckInGambit } from '@/views/main/gambits/utils/gambit-check-in-window';
import { useClipboard } from '@vueuse/core';
import { Chess } from 'chess.js';
import { storeToRefs } from 'pinia';
import { computed, nextTick, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useToast } from 'vue-toastification';
import MapProposalCard from '../locations/cards/MapProposalCard.vue';
import AboutLocation from '../locations/details/tabs/AboutLocation.vue';
import LocationChessFeatures from '../locations/details/tabs/LocationChessFeatures.vue';
import LocationGambits from '../locations/details/tabs/LocationGambits.vue';
import CheckInsList from '../locations/widgets/CheckInsList.vue';
import {
  hasPlantedBoard,
  hasPlayedBoard,
  isEligibleForMove,
  isWaitingForMove,
  sortSpotBoards,
} from '../locations/widgets/spot-board-rank';

const route = useRoute();
const router = useRouter();
const locationStore = useLocationStore();
const authStore = useAuthStore();
const mapStore = useMapStore();
const navigationStore = useNavigationStore();
const sparkStore = useSparkStore();
const chessboardStore = useChessboardStore();
const geolocationStore = useGeolocationStore();

const { location } = storeToRefs(locationStore);
const { user: authUser, userDetails, unitType } = storeToRefs(authStore);
const { userLocation, isLocationPermissionGranted, isLocationPermissionDenied } = storeToRefs(geolocationStore);
const distanceFix = computed(() => (isLocationPermissionGranted.value ? userLocation.value : null));
const { copy } = useClipboard();

const spotCoords = computed(() => location.value?.coordinates);
const access = useAccessLevel(spotCoords);
const awaitingLocation = ref(false);

type SpotView = 'detail' | 'history';

const spotView = ref<SpotView>('detail');
const activeBoards = ref<ChessboardDto[]>([]);
const completedBoards = ref<ChessboardDto[]>([]);
const isSubmittingCheckIn = ref(false);
const locationImages = ref<ImageDto[]>([]);
const expandImageConfig = ref<GamExpandImageType | null>(null);
const imageExpandRef = ref<InstanceType<typeof GamExpandImage>>();
const fileInputRef = ref<HTMLInputElement>();
const showReportDialog = ref(false);
const spotQrDialog = ref<InstanceType<typeof GamDialog>>();

const loadImages = async (id: string) => {
  const result = await gambits.locationService.getLocationImages(id);
  locationImages.value = result?.data ?? [];
};

const handleOpenGallery = () => {
  if (!locationImages.value.length) {
    if (authUser.value) handleAddPhotoClick();
    return;
  }
  const images = [...locationImages.value];
  const coverHash = location.value?.coverBlobHash;
  if (coverHash) {
    const pinIdx = images.findIndex((img) => img.imageBlobHash === coverHash);
    if (pinIdx > 0) {
      const [pinned] = images.splice(pinIdx, 1);
      images.unshift(pinned);
    }
  }
  expandImageConfig.value = {
    blobSource: BlobSource.LOCATION,
    selectedIndex: 0,
    images,
    noFilter: true,
    canDelete: false,
    canReport: true,
    canLike: false,
  };
  nextTick(() => imageExpandRef.value?.show());
};

const handleAddPhotoClick = () => {
  if (isUploading.value) return;
  fileInputRef.value?.click();
};

const isUploading = ref(false);

const handleFilesSelected = async (e: Event) => {
  const input = e.target as HTMLInputElement;
  if (!input.files?.length || !location.value) return;

  const locId = location.value.id;
  isUploading.value = true;

  const uploadedIds: string[] = [];
  for (const file of Array.from(input.files)) {
    const base64 = await FormHelper.readFileAsBase64(file);
    const dataUrl = `data:${file.type};base64,${base64}`;
    const result = await gambits.locationService.addLocationPhoto(locId, dataUrl);
    if (result.data?.id) uploadedIds.push(result.data.id);
  }
  input.value = '';
  await loadImages(locId);
  isUploading.value = false;

  const firstUploadedIndex = uploadedIds.length
    ? locationImages.value.findIndex((img) => uploadedIds.includes(img.id))
    : -1;

  if (route.query.photo) {
    await router.replace({ path: route.path, query: { ...route.query, photo: undefined } });
  }

  expandImageConfig.value = {
    blobSource: BlobSource.LOCATION,
    selectedIndex: firstUploadedIndex >= 0 ? firstUploadedIndex : locationImages.value.length - 1,
    images: [...locationImages.value],
    noFilter: true,
    canDelete: false,
    canReport: true,
    canLike: false,
  };
  nextTick(() => imageExpandRef.value?.show());
};

const pinnedCoverHash = ref<string | null>(null);

const handlePinCover = async (imageBlobHash: string | null) => {
  if (!location.value) return;
  await gambits.locationService.pinCover(location.value.id, imageBlobHash);
  pinnedCoverHash.value = imageBlobHash;
  useToast().info(imageBlobHash ? localize('spot.cover.pinned') : localize('spot.cover.unpinned'));
};

const handleDeletePhoto = async (blobHash: string) => {
  const image = locationImages.value.find((img) => img.imageBlobHash === blobHash);
  if (!image) return;
  await gambits.locationService.deleteLocationImage(image.id);
  if (location.value) await loadImages(location.value.id);
  expandImageConfig.value = null;
};

const spotId = computed(() => {
  const val = route.query.spot;
  return typeof val === 'string' ? val : null;
});

const isOpen = computed(() => !!spotId.value);

const loadBoards = async (id: string) => {
  const [activeResult, completedResult] = await Promise.all([
    chessboardStore.fetchActiveByLocation(id),
    chessboardStore.fetchCompletedByLocation(id),
  ]);
  activeBoards.value = activeResult?.data ?? [];
  completedBoards.value = completedResult?.data ?? [];
};

watch(
  spotId,
  async (id) => {
    if (id) {
      locationStore.fetch(id);
      loadImages(id);
      loadBoards(id);
      if (geolocationStore.isLocationPermissionGranted) {
        awaitingLocation.value = true;
        await geolocationStore.requestFreshPosition();
        awaitingLocation.value = false;
      }
    } else {
      locationStore.clear();
      locationImages.value = [];
      activeBoards.value = [];
      completedBoards.value = [];
      spotView.value = 'detail';
    }
  },
  { immediate: true },
);

watch(
  () => mapStore.proposalMode,
  (active, wasActive) => {
    if (!active && wasActive && spotId.value) {
      locationStore.fetch(spotId.value);
    }
  },
);

// The pill is kept in session storage, so the loaded spot is the authority on whether it still stands.
watch(
  () => [location.value?.id, location.value?.isCheckedIn] as const,
  ([id, isCheckedInAtSpot]) => {
    if (!id || typeof isCheckedInAtSpot !== 'boolean') return;
    navigationStore.reconcileActiveCheckIn(id, isCheckedInAtSpot);
  },
  { immediate: true },
);

const circleLocation = computed((): BaseLocationDto | null => {
  if (!location.value) return null;
  return {
    id: location.value.id,
    coordinates: location.value.coordinates,
    type: location.value.type,
    chessFeatures: location.value.chessFeatures,
    generalFeatures: location.value.generalFeatures,
    private: location.value.private,
    favorite: location.value.favorite,
    isVetted: location.value.isVetted,
    name: location.value.name,
    address: location.value.address,
    timezone: location.value.timezone,
    coverBlobHash: location.value.coverBlobHash,
    activeChessboardCount: activeBoards.value.length,
    nextGambits: location.value.nextGambits,
    visitors: location.value.visitors?.map((v) => ({
      id: v.id,
      username: v.username,
      firstName: '',
      lastName: '',
      profileImageBlobHash: v.profileImageBlobHash,
    })),
  };
});

const hasIrlActivity = computed(() => {
  return (location.value?.nextGambits?.length ?? 0) > 0;
});

const activeCheckIns = computed(() => {
  if (!location.value?.visitors) return [];
  return location.value.visitors.map((v) => ({ ...v, isCheckedIn: true }));
});

const getDistanceLabel = (coords: GeoCoords, user: { location: GeoCoords }) => {
  const distance = haversineDistance(coords, user.location);
  if (distance < 10) return localize('spot.distance.here');
  if (distance >= 100_000) return localize('spot.distance.far_away');
  return formatDistance(distance, unitType.value).join('');
};

const hasChessSet = computed(() => {
  if (!location.value?.visitors || !authUser.value?.id) return false;
  const me = location.value.visitors.find((v) => v.id === authUser.value?.id);
  return (me?.chessSetCount ?? 0) > 0;
});

const toggleChessSet = async () => {
  if (!location.value) return;
  const newCount = hasChessSet.value ? 0 : 1;
  await gambits.locationService.updateCheckIn(location.value.id, { chessSetCount: newCount });
  locationStore.fetch(location.value.id);
};

const drawerRef = ref<HTMLElement | null>(null);

const handleClose = () => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { spot: _spot, location: _loc, ...rest } = route.query;
  router.replace({ ...route, query: rest });
};

useDrawerGesture(drawerRef, handleClose);

const primaryBoardMarks = computed(() => {
  const board = sortSpotBoards(activeBoards.value, authUser.value?.id)[0];
  if (!board) return null;
  const userId = authUser.value?.id;
  return {
    eligible: isEligibleForMove(board, userId),
    waiting: isWaitingForMove(board, userId),
    planted: hasPlantedBoard(board, userId),
    played: hasPlayedBoard(board, userId),
  };
});

const openPrimaryBoard = async () => {
  if (!location.value) return;
  // The reading already on hand is enough when it is within 50 m, same as the old cover.
  // A farther reading only refreshes; the next tap opens once that fix is close.
  if (!access.isNearby.value) {
    awaitingLocation.value = true;
    await geolocationStore.requestFreshPosition();
    awaitingLocation.value = false;
    if (!access.isNearby.value) {
      useToast().info(localize('spot.proximity.get_closer'));
    }
    return;
  }
  const ranked = sortSpotBoards(activeBoards.value, authUser.value?.id);
  const board = ranked[0];
  if (!board) return;
  const ids = ranked.map((item) => item.id).join(',');
  router.push({ path: `/board/${board.id}`, query: { boards: ids, loc: location.value.id } });
};

const handleBoardsIcon = () => {
  requestLocationFor('boards', () => {
    void openPrimaryBoard();
  });
};

const openHistory = () => {
  spotView.value = 'history';
};

const closeHistory = () => {
  spotView.value = 'detail';
};

const getResultLabel = (result?: ChessboardResult, fen?: string): string => {
  switch (result) {
    case ChessboardResult.WHITE_WIN:
    case ChessboardResult.BLACK_WIN: {
      if (fen) {
        try {
          const chess = new Chess(fen);
          if (chess.isCheckmate()) return localize('chessboard.demo.checkmate');
        } catch {
          /* ignore */
        }
      }
      return localize('chessboard.demo.resignation');
    }
    case ChessboardResult.DRAW:
      return localize('chessboard.demo.draw');
    case ChessboardResult.STALEMATE:
      return localize('chessboard.demo.stalemate');
    default:
      return '';
  }
};

const handleBoardClick = (boardId: string) => {
  router.push({ path: `/board/${boardId}` });
};

const handleCheckIn = async () => {
  if (!location.value || isSubmittingCheckIn.value) return;

  // Checking in through a gambit makes the user an attendee, so it is exempt from the proximity gate.
  const activeGambit = findActiveCheckInGambit(location.value.nextGambits);

  if (!location.value.isCheckedIn && !activeGambit) {
    if (!geolocationStore.isLocationPermissionGranted) {
      requestLocationFor('checkin', () => {
        void handleCheckIn();
      });
      return;
    }
    if (!access.hasGps.value) {
      const coords = await geolocationStore.requestFreshPosition();
      if (!coords) {
        if (isLocationPermissionDenied.value) {
          notifyLocationDenied('checkin');
        } else {
          notifyLocationUnavailable('checkin');
        }
        return;
      }
    }
    if (!access.canCheckIn.value) {
      useToast().info(localize('spot.proximity.get_closer'));
      return;
    }
  }

  const loc = location.value;
  isSubmittingCheckIn.value = true;

  let refreshGambitId: string | undefined;

  if (loc.isCheckedIn) {
    refreshGambitId = navigationStore.activeCheckIn?.gambitId;
    await gambits.locationService.checkOut(loc.id);
    gambits.locationService.updateMarkerCheckInStatus(loc.id, false);
    navigationStore.clearActiveCheckIn();
  } else {
    const options = activeGambit ? { gambitId: activeGambit.id } : { ignoreGambit: true };
    const result = await gambits.locationService.checkIn(loc.id, options);
    if (result?.error) {
      isSubmittingCheckIn.value = false;
      return;
    }
    // A gambit check-in never asks for location, so it does not teach the sentence.
    if (!activeGambit) rememberLocationFunction();
    gambits.locationService.updateMarkerCheckInStatus(loc.id, true);
    navigationStore.setActiveCheckIn({
      locationId: loc.id,
      locationName: loc.name,
      coordinates: loc.coordinates,
      gambitId: activeGambit?.id,
      // The server may return an existing check-in, whose start is what it expires from.
      checkedInAt: result?.data?.start ?? new Date().toISOString(),
    });
    refreshGambitId = activeGambit?.id;
  }

  await locationStore.fetch(loc.id);
  if (refreshGambitId) {
    const gambitStore = useGambitStore();
    if (gambitStore.gambit?.id === refreshGambitId) {
      gambitStore.fetch(refreshGambitId).catch(() => {});
    }
  }
  isSubmittingCheckIn.value = false;
};

const isProposalOpen = ref(false);
const proposalLocation = computed((): MapLocationDto | null => {
  if (!location.value) return null;
  const loc = location.value;
  return {
    mapLocationType: MapLocationType.LOCATION,
    id: loc.id,
    coordinates: loc.coordinates,
    type: loc.type,
    name: loc.name,
    address: loc.address,
    coverBlobHash: loc.coverBlobHash,
    visitorCount: loc.visitors?.length ?? 0,
    nextGambits: loc.nextGambits?.map((g) => ({
      id: g.id,
      name: g.title,
      coordinates: { lat: g.locationPoint.lat, lng: g.locationPoint.lng },
      start: g.start,
      end: g.end,
      category: g.gambitCategory,
      proposal: g.proposal,
    })),
  };
});

const handlePlayChess = () => {
  if (!location.value) return;
  isProposalOpen.value = true;
};

const handleProposalClose = () => {
  isProposalOpen.value = false;
};

const handleProposalCreated = async () => {
  isProposalOpen.value = false;
  if (location.value?.id) {
    await locationStore.fetch(location.value.id);

    if (location.value) {
      const loc = location.value;
      const updatedMarker: MapLocationDto = {
        mapLocationType: MapLocationType.LOCATION,
        id: loc.id,
        coordinates: loc.coordinates,
        type: loc.type,
        name: loc.name,
        address: loc.address,
        nextGambits: loc.nextGambits?.map((g) => ({
          id: g.id,
          name: g.title,
          coordinates: { lat: g.locationPoint.lat, lng: g.locationPoint.lng },
          start: g.start,
          end: g.end,
          category: g.gambitCategory,
          proposal: g.proposal,
        })),
        visitorCount: loc.visitors?.length ?? 0,
      };
      gambits.locationService.updateMarker(updatedMarker, loc.id);
    }
  }
};

const handleToggleFollow = () => {
  if (!location.value) return;

  const newFavorite = !location.value.favorite;
  location.value = { ...location.value, favorite: newFavorite };

  gambits.locationService.toggleFavorite(location.value.id, newFavorite);
  gambits.locationService.updateMarkerFavoriteStatus(location.value.id, newFavorite);
  sparkStore.reloadSparkableLocations();
  sparkStore.fetchIntents({ refreshExisting: true });
};

const GetCoordinateString = (round?: boolean): string => {
  if (!location.value) return '';
  if (round) {
    const coords = LocationHelper.roundLocation(location.value.coordinates, 4);
    return `${coords.lat}, ${coords.lng}`;
  }
  return `${location.value.coordinates.lat}, ${location.value.coordinates.lng}`;
};

const getMoreItems = computed((): GamMoreType => {
  const items = [
    {
      label: 'app.more.navigate',
      onClick: () => {
        LocationHelper.redirectToGoogleMaps(location.value?.coordinates);
      },
    },
    {
      label: GetCoordinateString(true),
      onClick: () => {
        copy(GetCoordinateString()).then(() => useToast().info('Copied.'));
      },
    },
    {
      label: 'app.more.copy.address',
      onClick: () => {
        if (location.value?.address) {
          copy(location.value.address).then(() => useToast().info('Copied.'));
        }
      },
    },
    {
      label: 'app.more.share.location',
      onClick: () => {
        const spotUrl = `${window.location.origin}/spot/${location.value?.id}`;
        copy(spotUrl).then(() => useToast().info('Copied.'));
      },
    },
    {
      label: 'app.more.qrcode',
      onClick: () => spotQrDialog.value?.show(),
    },
    {
      label: 'app.component.image.add.label',
      onClick: () => handleAddPhotoClick(),
    },
    {
      label: 'app.more.report',
      onClick: () => (showReportDialog.value = true),
    },
  ];

  if (userDetails.value?.isAdmin) {
    items.push({
      label: 'app.more.edit',
      onClick: () => {
        router.push({
          ...route,
          query: { ...route.query, side_panel: 'edit_location', spot: location.value?.id },
        });
      },
    });
    items.push({
      label: 'app.more.delete',
      onClick: async () => {
        const name = location.value?.name ?? 'this spot';
        if (!window.confirm(`Are you sure you want to delete ${name}?`)) return;
        await useAdminStore().deleteLocation(location.value?.id);
        handleClose();
      },
    });
  }

  return {
    items,
    size: GamButtonSize.SMALL,
    variant: GamButtonVariant.STEKLO,
    position: GamMorePosition.LEFT_BELOW,
    icon: GamIconName.DOTS_V,
  };
});
</script>

<style scoped lang="scss">
@use '@/ui/css/mixins' as *;
.SpotOverlay {
  position: fixed;
  inset: 0;
  z-index: 500;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
}

.spot-card {
  position: relative;
  width: 380px;
  max-width: calc(100vw - 2rem);
  height: min(650px, calc(100vh - 4rem));
  border-radius: 1.5rem;
  border: 1px solid var(--color-white-10);
  background: var(--color-dark-700);
  display: flex;
  flex-direction: column;
  overflow: hidden;

  &.is-mobile {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    width: 100%;
    max-width: 100%;
    height: 85vh;
    border-radius: 1.5rem 1.5rem 0 0;
    will-change: transform;

    .spot-scroll {
      padding-bottom: var(--safe-bottom-offset);
    }
  }
}

.spot-scroll {
  flex: 1;
  overflow-x: hidden;
  overflow-y: auto;
  scrollbar-width: none;
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;

  &::-webkit-scrollbar {
    display: none;
  }
}

.spot-header {
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
}

.spot-actions {
  position: relative;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  min-height: 3.5rem;
  margin-bottom: 0.75rem;
  overflow: visible;
}

.spot-action-play {
  position: relative;
  z-index: 1;
}

// One play-radius, the old 1rem gap, and one check-in radius. Play stays on the
// card's center line; check-in keeps its 3rem size to the right.
.spot-action.spot-action-checkin {
  position: absolute;
  top: 0.25rem;
  left: calc(50% + 4.25rem);
  transform: translateX(-50%);
}

.spot-circle {
  cursor: pointer;
  --circle-size: 96px;
  width: auto;
  max-width: 100%;
  gap: 0.75rem;

  :deep(.circle-name) {
    font-size: 17px;
    -webkit-line-clamp: 1;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 280px;
  }
}

.spot-meta {
  margin-top: 0.25rem;
}

.spot-distance {
  font-size: 12px;
  color: var(--color-white-50);
  letter-spacing: 0.04em;
  display: flex;
  align-items: center;
  gap: 4px;
}

.spot-distance-refreshing {
  display: inline-block;
  font-size: 12px;
  animation: spin-refresh 1s linear infinite;
}

@keyframes spin-refresh {
  to {
    transform: rotate(360deg);
  }
}

.spot-header-actions {
  position: absolute;
  top: 0;
  right: 0;
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.spot-boards-header-btn {
  position: absolute;
  top: 0;
  left: 0;
  width: 64px;
  height: 64px;
  border-radius: 10px;
  overflow: hidden;
  border: 1px solid var(--color-white-10);
  cursor: pointer;

  @include hover {
    border-color: var(--color-white-30);
  }
}

.spot-board-squares {
  width: 100%;
  height: 100%;
  background-repeat: no-repeat;
  background-size: 50% 50%;
  background-position:
    0 0,
    100% 0,
    0 100%,
    100% 100%;
  background-image: linear-gradient(#f0d9b5, #f0d9b5), linear-gradient(#b58863, #b58863),
    linear-gradient(#b58863, #b58863), linear-gradient(#f0d9b5, #f0d9b5);
}

.spot-board-dot {
  position: absolute;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.55);

  &.is-eligible {
    top: 4px;
    left: 4px;
    background: #2ecc71;
  }

  &.is-waiting {
    top: 4px;
    left: 4px;
    background: #f1c40f;
  }

  &.is-planted {
    top: 4px;
    right: 4px;
    background: var(--color-pink);
  }

  &.is-played {
    right: 4px;
    bottom: 4px;
    background: var(--color-white);
  }
}

.spot-follow-header-btn {
  width: 2rem;
  height: 2rem;
  border-radius: 2rem;
  border: 1px solid var(--color-white-10);
  background: rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(15px);
  -webkit-backdrop-filter: blur(15px);
  color: var(--color-white-70);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 150ms linear;

  @include hover {
    background: rgba(255, 255, 255, 0.2);
    border-color: var(--color-white-30);
  }

  &.following {
    color: var(--color-white);
    border-color: var(--color-white);
  }
}

.spot-action {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.375rem;
  cursor: pointer;
  position: relative;

  @include hover-capable {
    &:hover .spot-action-btn {
      background-color: var(--color-dark-20);
      border-color: var(--color-white-30);
      color: var(--color-white);
    }
  }
}

.spot-action-label {
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--color-white-50);
  white-space: nowrap;
}

.spot-action-checkin .spot-action-label.is-on {
  color: var(--color-white);
}

.spot-action-btn {
  width: 3rem;
  height: 3rem;
  border-radius: 3rem;
  border: 1px solid var(--color-white-10);
  backdrop-filter: blur(8px);
  background-color: var(--color-dark-5);
  color: var(--color-white);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 200ms linear;

  &.disabled {
    opacity: 0.35;
    cursor: not-allowed;
  }

  &.play-btn {
    width: 3.5rem;
    height: 3.5rem;
    background: var(--color-linear-gradient-30), rgba(0, 0, 0, 1);
    border-color: transparent;
    color: var(--color-white);
    box-shadow: var(--shadow-button-action);

    @include hover {
      background: var(--color-linear-gradient-30), rgba(255, 255, 255, 0.25);
    }

    :deep(svg) {
      transform: translateX(1px);
    }
  }

  @include press;
}

.spot-checkin-btn {
  width: 3rem;
  height: 3rem;
  @include toolbar-action;
}

.spot-boards-header-row {
  display: flex;
  align-items: center;
  gap: 0.375rem;
}

.spot-boards-title {
  font-size: 12px;
  font-weight: 600;
  color: var(--color-white);
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.spot-history-btn {
  display: block;
  text-align: left;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  cursor: pointer;
  color: var(--color-white-50);
  transition: color 150ms ease;

  @include hover {
    color: var(--color-white);
  }
}

.spot-history-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.spot-history-back {
  width: 1.75rem;
  height: 1.75rem;
  border-radius: 1.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: var(--color-white-50);
  background: var(--color-white-5);
  transition: all 150ms ease;

  @include hover {
    color: var(--color-white);
    background: var(--color-white-10);
  }
}

.spot-history-empty {
  text-align: center;
  padding: 2rem 0;
  color: var(--color-white-50);
  font-size: 13px;
}

.spot-history-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem 0;
}

.spot-history-loading-text {
  color: var(--color-white-50);
  font-size: 14px;
  letter-spacing: 0.2em;
}

.spot-history-list {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.spot-history-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.625rem 0.75rem;
  border-radius: 0.5rem;
  cursor: pointer;
  transition: background 150ms ease;

  @include hover {
    background: var(--color-white-5);
  }
}

.spot-history-result {
  font-size: 13px;
  font-weight: 600;
  color: var(--color-white);
}

.spot-history-meta {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 12px;
  color: var(--color-white-50);
}

.spot-remote-banner {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-l);
  padding: var(--spacing-xxl) var(--spacing-l);
  color: var(--color-white-50);
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.04em;
}

.spot-section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.spot-chess-set {
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  user-select: none;
  color: var(--color-white);
}

.spot-chess-check {
  width: 18px;
  height: 18px;
  border-radius: 4px;
  border: 1.5px solid var(--color-white-30);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 150ms ease;

  &.is-checked {
    background: var(--color-pink);
    border-color: var(--color-pink);
  }
}

.spot-chess-label {
  font-size: 12px;
  font-weight: 600;
}

.spot-section {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.spot-live-panel,
:deep(.location-gambits-tab) {
  border-radius: 0.75rem;
  padding: 0.75rem 1rem;
  background: var(--color-white-5);
}

.spot-section-title {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: var(--color-white);
}

.spot-section-line {
  border: none;
  border-top: 1px solid var(--color-white-10);
  margin: 0;
}

:deep(.location-gambits-tab) {
  .sub-head h5 {
    font-size: 12px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    color: var(--color-white);
    margin: 0 0 0.5rem;
  }

  .sub-head hr {
    border: none;
    height: 1px;
    background: var(--color-white-10);
    margin: 0 0 0.25rem;
  }
}

.spot-close {
  position: absolute;
  bottom: var(--safe-bottom-offset);
  left: 50%;
  transform: translateX(-50%);
  width: 3.5rem;
  height: 3.5rem;
  border-radius: 3.5rem;
  border: 1px solid var(--color-white-10);
  backdrop-filter: blur(8px);
  background-color: var(--color-dark-5);
  color: var(--color-white-70);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 200ms linear;
  z-index: 1;

  @include hover {
    background-color: var(--color-dark-20);
    border-color: var(--color-white-30);
    color: var(--color-white);
  }

  @include press($transform: translateX(-50%));
}

.spot-overlay-enter-active,
.spot-overlay-leave-active {
  transition: opacity 200ms ease;

  .spot-card {
    transition: transform 200ms ease;
  }
}

.spot-overlay-enter-from,
.spot-overlay-leave-to {
  opacity: 0;

  .spot-card {
    transform: translateY(20px);
  }
}

.spot-overlay-enter-from .is-mobile,
.spot-overlay-leave-to .is-mobile {
  transform: translateY(100%);
}

// --- Proposal Drawer ---

.spot-proposal-drawer {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 10;
  border-radius: var(--radius-large) var(--radius-large) 0 0;
  overflow: hidden;
  background: var(--color-dark-700);
  border-top: 1px solid var(--color-white-10);
}

.proposal-drawer-enter-active,
.proposal-drawer-leave-active {
  transition: transform 250ms ease;
}

.proposal-drawer-enter-from,
.proposal-drawer-leave-to {
  transform: translateY(100%);
}
</style>
